import React, { useEffect, useId, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import useFeatureToggle from "@/hooks/use-feature-toggle/use-feature-toggle";
import {
  prepareFormDataSubmitted,
  SubmissionData
} from "@/utils/hubspot-utils/hubspot-embed-utils";
import {
  CustomMultiSelectFormElement,
  CustomHubspotDropdown,
  HubspotFormHiddenField
} from "@/types/hubspot/hubspot-form";
import {
  setupLiveEmailValidation,
  setupLivePhoneValidation,
  validateEmails,
  validatePhone
} from "@/utils/hubspot-utils/hubspot-validation";
import { Locale } from "@/i18n/routing";
import useGaSessionInfo from "../ga-session/use-ga-session";

interface UseHubspotEmbedProps {
  formId: string;
  portalId: string;
  formName: string;
  targetId?: string;
  customDropdownOptions?: CustomHubspotDropdown[];
  hiddenFields?: HubspotFormHiddenField[];
  leadServiceOnlyFields?: HubspotFormHiddenField[];
  onSubmit?: () => void;
  onSubmitWithFormData?: (formData: SubmissionData) => void;
  customCSS?: string;
  customMultiSelectFormElement?: CustomMultiSelectFormElement;
  instanceId?: string;
  brochureInfo?: {
    brochureName: string;
    brochureQuantity: number;
  };
}

const HUBSPOT_FORMS_VENDOR_ID = "s10";
const HUBSPOT_FORMS_SCRIPT_SRC = "https://js.hsforms.net/forms/v2.js";
const CONSENT_MANAGER_FLAG_KEY = "ConsentManager";
const CMP_CONSENT_EVENTS = ["consentapproved", "consentcustom"] as const;

type CmpApi = (command: string, parameter?: unknown, callback?: unknown) => unknown;
type WindowWithCmp = Window & { __cmp?: CmpApi };

const isGrantedConsentValue = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === "string")
    return ["1", "true", "yes", "granted"].includes(value.toLowerCase());
  return false;
};

const readVendorConsent = (cmpData: unknown, vendorId: string): boolean => {
  if (!cmpData || typeof cmpData !== "object") return false;

  const data = cmpData as Record<string, any>;
  const vendorConsentSources = [
    data.vendorConsents,
    data.vendorConsents?.[vendorId],
    data.vendors?.[vendorId],
    data.vendorList?.[vendorId],
    data.consents?.vendors?.[vendorId],
    data.consents?.[vendorId]
  ];

  return vendorConsentSources.some((source) => {
    if (Array.isArray(source)) return source.includes(vendorId);
    if (source && typeof source === "object") {
      return (
        isGrantedConsentValue(source[vendorId]) ||
        isGrantedConsentValue(source.consent) ||
        isGrantedConsentValue(source.status) ||
        isGrantedConsentValue(source.enabled)
      );
    }
    return isGrantedConsentValue(source);
  });
};

const hasCmpVendorConsent = (vendorId: string) => {
  const cmp = (window as WindowWithCmp).__cmp;
  if (!cmp) return false;

  try {
    return readVendorConsent(cmp("getCMPData"), vendorId);
  } catch {
    return false;
  }
};

const useHubspotEmbed = ({
  formId,
  portalId,
  formName,
  targetId,
  customDropdownOptions,
  hiddenFields,
  leadServiceOnlyFields,
  customCSS,
  onSubmit,
  onSubmitWithFormData,
  customMultiSelectFormElement,
  instanceId,
  brochureInfo
}: UseHubspotEmbedProps) => {
  const [dataLayerProps, setDataLayerProps] = useState<Record<string, any> | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const {
    isEnabled: isConsentManagerEnabled,
    isLoading: isConsentManagerLoading,
    isSuccess: isConsentManagerResolved,
    isError: isConsentManagerError
  } = useFeatureToggle({
    flagKey: CONSENT_MANAGER_FLAG_KEY,
    namespaceKey: "ColletteWeb"
  });
  const isConsentManagerReady = isConsentManagerResolved || isConsentManagerError;
  const isCmpEnabledForScript = isConsentManagerEnabled || isConsentManagerError;

  const locale = useLocale() as Locale;
  const t = useTranslations("DESTINATIONS");
  const tForms = useTranslations("FORMS");


  const hookId = useId().replace(/:/g, "");
  const resolvedTargetId = targetId ?? `hubspotForm-${formId}-${hookId}`;

  
  const submissionHandledRef = React.useRef(false);
  const messageHandledRef = React.useRef(false);
  // Several instances of this hook can share one HubSpot formId on a page (for example two
  // "Talk to an Expert" CTAs, each with its own dialog). HubSpot posts one window message per
  // submission with only the formId in it, so every instance receives it. Only the instance whose
  // own form was submitted should show the confirmation, call onSubmit, and post to lead-service.
  const submittedFromThisInstance = React.useRef(false);

  const submittedValuesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!customMultiSelectFormElement) return;
    const el = document.getElementById(customMultiSelectFormElement?.hiddenField.id);
    if (el) {
      const checked = customMultiSelectFormElement.options
        .filter(({ value }) => value)
        .map((value) => value.valueToSend);
      el.setAttribute("value", checked.join(", "));
      // const isValid = checked.length > 0 && checked.length <= 3;
    }
  }, [customMultiSelectFormElement, customMultiSelectFormElement?.options, formId]);

  const { clientId, sessionId, measurementId } = useGaSessionInfo();

  const measurementIdRef = useRef(measurementId);

  useEffect(() => {
    measurementIdRef.current = measurementId;
  }, [measurementId]);

  useEffect(() => {
    if (isConsentManagerLoading || !isConsentManagerReady) return undefined;

    let disposed = false;
    let cmpPoll: ReturnType<typeof setInterval> | undefined;
    const cmpListeners: Array<{ eventName: string; handler: () => void }> = [];

    const removeCmpListeners = () => {
      const cmp = (window as WindowWithCmp).__cmp;
      if (!cmp) return;

      cmpListeners.forEach(({ eventName, handler }) => {
        cmp("removeEventListener", [eventName, handler, false], null);
      });
      cmpListeners.length = 0;
    };

    const showSubmissionMessage = () => {
      const container = document.getElementById(resolvedTargetId);
      if (!container) return;

      const form = container.querySelector("form") as HTMLElement | null;
      if (form) form.style.display = "none";

      if (container.querySelector(".submitted-message, .hs-form__success")) return;
      if (container.querySelector("[data-testid='hubspot-submission-message']")) return;

      const message = document.createElement("div");
      message.setAttribute("data-testid", "hubspot-submission-message");
      message.setAttribute("role", "status");
      message.setAttribute("aria-live", "polite");
      message.textContent = tForms("Common Thank You");
      message.style.padding = "2rem 0";
      message.style.textAlign = "center";
      message.style.fontSize = "1.8rem";
      message.style.fontWeight = "600";
      container.appendChild(message);
    };

    const createHubspotForm = () => {
      if (disposed) return;
      if (window.hbspt) {
        const markSubmissionHandled = () => {
          if (submissionHandledRef.current) return;

          submissionHandledRef.current = true;
          setIsSubmitted(true);
          onSubmit?.();
        };

        window.hbspt.forms.create({
          portalId: portalId || "2641890", // Default to Collette's portal ID if not provided
          formId,
          target: `#${resolvedTargetId}`,
          css: customCSS || "",
          formInstanceId: instanceId,
          onFormSubmit() {
            submittedFromThisInstance.current = true;

            const formElement = document.querySelector(
              `#${resolvedTargetId} form`
            ) as HTMLFormElement | null;

            if (formElement) {
              submittedValuesRef.current = Object.fromEntries(
                Array.from(new FormData(formElement).entries()).map(([name, value]) => [
                  name,
                  String(value)
                ])
              );

              console.log("submittedValuesRef.current:", submittedValuesRef.current);
            }
          },
          onFormSubmitted() {
            if (submittedFromThisInstance.current) {
              markSubmissionHandled();
            }
          },
          onFormReady() {
            const form = document.querySelector(`#${resolvedTargetId} form`) as HTMLFormElement;
            if (!form) return;

            // Add attributes to the form itself
            form.setAttribute("data-form-name", formName);
            form.setAttribute("data-form-id", formId);
            form.setAttribute("data-track", "visibility");
            setupLiveEmailValidation(form);
            setupLivePhoneValidation(form, locale, t);

            // Add data-testid to every field in the form
            Array.from(form.elements).forEach((field) => {
              if (
                field instanceof HTMLInputElement ||
                field instanceof HTMLSelectElement ||
                field instanceof HTMLTextAreaElement
              ) {
                const fieldName = field.name || field.id || "unknown-field";

                field.setAttribute("data-testid", `${formName}-${fieldName}`);
              }
            });
            const submitButton = form.querySelector("input[type=\"submit\"]") as HTMLInputElement;

            if (submitButton) {
              submitButton.setAttribute("data-testid", `${formName}-submit-button`);
            }

            if (customDropdownOptions) {
              customDropdownOptions.forEach(({ id, options, defaultValue }) => {
                const dropdown = document.getElementById(id) as HTMLSelectElement;
                if (dropdown) {
                  options
                    .filter((option) => option.type === "add")
                    .forEach(({ value, textContent }) => {
                      const option = document.createElement("option");
                      option.value = value;
                      option.textContent = textContent;
                      if (defaultValue === value) {
                        option.selected = true;
                      }
                      dropdown.appendChild(option);
                    });
                  options
                    .filter((option) => option.type === "remove")
                    .forEach(({ value }) => {
                      const array = [...dropdown.options];
                      const option = array.findIndex((el) => el.value === value);
                      if (option) {
                        dropdown.remove(option);
                      }
                    });
                }
              });
            }

            if (hiddenFields) {
              hiddenFields.forEach(({ id, value }) => {
                const el = document.getElementById(id);
                if (el) {
                  el.setAttribute("value", value);
                }
              });
            }

            // Intercept the form submission
            form.addEventListener("submit", (e) => {
              const isValid = validateEmails(form);
              if (!isValid) {
                e.preventDefault(); // Stop submission
                e.stopImmediatePropagation(); // In case HubSpot attaches its own handler
                return;
              }

              submittedFromThisInstance.current = true;
            });
          }
        });
      }
      setIsLoading(false);
    };

    const appendHubspotScript = () => {
      if (disposed) return;

      setIsLoading(true);
      const script = document.createElement("script");
      script.src = HUBSPOT_FORMS_SCRIPT_SRC;
      script.addEventListener("load", createHubspotForm);
      document.body.appendChild(script);
    };

    const appendWhenVendorConsented = () => {
      if (!hasCmpVendorConsent(HUBSPOT_FORMS_VENDOR_ID)) return false;

      if (cmpPoll) clearInterval(cmpPoll);
      removeCmpListeners();
      appendHubspotScript();
      return true;
    };

    if (!isCmpEnabledForScript) {
      appendHubspotScript();
    } else if (!appendWhenVendorConsented()) {
      const attachCmpListeners = () => {
        const cmp = (window as WindowWithCmp).__cmp;
        if (!cmp) return false;

        CMP_CONSENT_EVENTS.forEach((eventName) => {
          const handler = () => {
            appendWhenVendorConsented();
          };
          cmp("addEventListener", [eventName, handler, false], null);
          cmpListeners.push({ eventName, handler });
        });
        appendWhenVendorConsented();
        return true;
      };

      if (!attachCmpListeners()) {
        cmpPoll = setInterval(() => {
          if (attachCmpListeners() && cmpPoll) {
            clearInterval(cmpPoll);
            cmpPoll = undefined;
          }
        }, 250);
      }
      setIsLoading(false);
    }

    const handleMessage = async (event: MessageEvent) => {
      const form = document.querySelector(`#${resolvedTargetId} form`);
      if (form) {
        validateEmails(form as HTMLFormElement);
        validatePhone(form as HTMLFormElement, locale, t);
      }
      if (
        event.data.type === "hsFormCallback" &&
        event.data.eventName === "onFormSubmitted" &&
        String(event.data.id) === String(formId) &&
        submittedFromThisInstance.current &&
        !messageHandledRef.current
      ) {
        messageHandledRef.current = true;

        const container = document.getElementById(resolvedTargetId);
        if (!container) return;

        container.style.alignContent = "center";

        showSubmissionMessage();

        if (!submissionHandledRef.current) {
          submissionHandledRef.current = true;
          setIsSubmitted(true);
          onSubmit?.();
        }

        console.log("HubSpot submission values:", event.data.data.submissionValues);

        const hubspotValues = event.data?.data?.submissionValues ?? {};

        const submissionValues = Object.fromEntries(
          Object.entries({
            ...submittedValuesRef.current,
            ...hubspotValues
          }).map(([name, value]) => [
            name,
            value === "" ? (submittedValuesRef.current[name] ?? "") : value
          ])
        );

        console.log("submissionValues:", submissionValues);

        const formData = prepareFormDataSubmitted(
          submissionValues,
          portalId,
          formId,
          locale,
          leadServiceOnlyFields,
          clientId ?? undefined,
          sessionId ?? undefined,
          measurementIdRef.current ?? undefined
        );

        if (onSubmitWithFormData) {
          onSubmitWithFormData(formData);
        }

        const url = "/api/lead-service?datasource=hubspot";
        const options = {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8"
          },
          body: JSON.stringify(formData)
        };

        await fetch(url, options);

        setDataLayerProps({
          event: "dataLayerPush",
          formData: {
            Type: "Hubspot",
            Id: formId,
            Location: window.location.href,
            Name:
              formName ||
              formData.fields.find((x: { name: string }) => x.name === "form_name")?.value,
            firstname:
              formData.fields.find((x: { name: string }) => x.name === "firstname")?.value || "",
            lastname:
              formData.fields.find((x: { name: string }) => x.name === "lastname")?.value || "",
            email: formData.fields.find((x: { name: string }) => x.name === "email")?.value || "",
            confirmEmail:
              formData.fields.find((x: { name: string }) => x.name === "confirm_email")?.value ||
              "",
            phone: formData.fields.find((x: { name: string }) => x.name === "phone")?.value || "",
            "yes i would like to receive email offers travel tips and destination information":
              formData.fields.find(
                (x: { name: string }) =>
                  x.name ===
                  "yes_i_would_like_to_receive_email_offers_travel_tips_and_destination_information_"
              )?.value || "",
            "yes i am a travel professional":
              formData.fields.find(
                (x: { name: string }) => x.name === "yes_i_m_a_travel_professional"
              )?.value || "false",
            "please contact me via":
              formData.fields.find((x: { name: string }) => x.name === "please_contact_me_via")
                ?.value || "",
            "preferred time to contact":
              formData.fields.find((x: { name: string }) => x.name === "preferred_time_to_contact_")
                ?.value || "",
            "are you a travel advisor":
              formData.fields.find((x: { name: string }) => x.name === "are_you_a_travel_advisor_")
                ?.value || "",
            "yes i am traveling with 8 or more people":
              formData.fields.find(
                (x: { name: string }) => x.name === "yes__i_m_traveling_with_8_or_more_people"
              )?.value || "",
            "preferred travel date":
              formData.fields.find((x: { name: string }) => x.name === "preferred_travel_date")
                ?.value || "",
            "preferred airport":
              formData.fields.find((x: { name: string }) => x.name === "raq_airport")?.value || "",
            "my dates are flexible":
              formData.fields.find((x: { name: string }) => x.name === "my_dates_are_flexible")
                ?.value || "",
            tourId:
              formData.fields.find((x: { name: string }) => x.name === "tour_id")?.value || "",
            ...(brochureInfo?.brochureName && {
              brochures: [
                {
                  brochureName: brochureInfo.brochureName,
                  brochureQuantity: brochureInfo.brochureQuantity || 0
                }
              ]
            })
          }
        });

      }
    };
    window.addEventListener("message", handleMessage);

    return () => {
      disposed = true;
      if (cmpPoll) clearInterval(cmpPoll);
      removeCmpListeners();
      window.removeEventListener("message", handleMessage);
    };
  }, [
    formId,
    isCmpEnabledForScript,
    isConsentManagerLoading,
    isConsentManagerReady,
    resolvedTargetId
  ]);

  return {
    dataLayerProps,
    isSubmitted,
    isLoading,
    targetId: resolvedTargetId
  };
};

export default useHubspotEmbed;
